import { useMemo } from 'react';

import { useDisputeAgent } from '~/components/disputes';
import useInvoice from '~/data/hooks/useInvoice';
import useInvoiceTickets from '~/data/hooks/useInvoiceTickets';
import type {
  ApproveInvoiceForm,
  Invoice,
  PartialApproveInvoiceForm,
  RejectInvoiceForm,
  ResetInvoiceForm,
} from '~/data/schemas/invoice';
import { InvoiceActionType, invoiceKindToLabelMap } from '~/data/schemas/invoice';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { useFeatureFlags } from '~/utils/featureFlags';

/**
 * The review-decision wiring behind `InvoiceActions` — approve, partial, reject, reset,
 * request-data — plus the linked tickets and dispute-agent state the detail surfaces
 * need alongside it.
 *
 * Extracted from `InvoiceDetailContainer` so the drawer and the full-page view share one
 * copy. Every action refreshes the dispute agent, since a decision can resolve or
 * invalidate an in-flight dispute.
 */
export function useInvoiceReviewActions(invoice: Invoice | undefined) {
  const { showEntityActionSnackbar } = useLAISnackbar();
  const featureFlags = useFeatureFlags();
  const showDisputeManagement = featureFlags.disputeManagement === true;

  const invoiceId = invoice?.id ?? null;
  const {
    reviewInvoice,
    invoiceNotes: { mutate: revalidateNotes },
  } = useInvoice(invoiceId);
  const { tickets } = useInvoiceTickets(invoiceId);

  const disputeAgent = useDisputeAgent(showDisputeManagement ? invoiceId : null);

  const invoiceActions = useMemo(
    () => ({
      onApprove: async (formData: ApproveInvoiceForm) => {
        await reviewInvoice(InvoiceActionType.APPROVE, formData);
        void disputeAgent.refreshState();
      },
      onReset: async (formData: ResetInvoiceForm) => {
        await reviewInvoice(InvoiceActionType.RESET, formData);
        void disputeAgent.refreshState();
        if (!invoice) return;
        showEntityActionSnackbar(
          {
            name: invoiceKindToLabelMap.get(invoice.kind)!,
            action: ActionEnum.Update,
            id: invoice.number,
          },
          { variant: 'info' },
        );
      },
      onReject: async (formData: RejectInvoiceForm) => {
        await reviewInvoice(InvoiceActionType.REJECT, formData);
        void disputeAgent.refreshState();
      },
      onPartial: async (formData: PartialApproveInvoiceForm) => {
        await reviewInvoice(InvoiceActionType.PARTIAL, formData);
        void disputeAgent.refreshState();
      },
      onRequestData: async () => {
        await reviewInvoice(InvoiceActionType.REQUEST_DATA);
        void disputeAgent.refreshState();
        if (!invoice) return;
        showEntityActionSnackbar(
          {
            name: invoiceKindToLabelMap.get(invoice.kind)!,
            action: ActionEnum.Update,
            id: invoice.number,
          },
          { variant: 'info' },
        );
      },
    }),
    [reviewInvoice, invoice, showEntityActionSnackbar, disputeAgent],
  );

  return { invoiceActions, revalidateNotes, tickets, disputeAgent };
}
